// ============================================================
// PLUMME — AI Rådgiver: budsjett mot faktisk
// ============================================================
// Ren avledning av budsjettlinjer og bokførte tall. Ingen Firebase-kall.
//
// Hele poenget med fila er én ting som er lett å få galt: bokførte tall er
// hittil i år, mens et budsjett er for hele året. Sammenlignes de direkte, ser
// hver eneste kostnadslinje ut som den ligger langt under budsjett i januar, og
// hver inntektslinje ser ut som en katastrofe. Vi summerer derfor budsjettets
// månedsbeløp fram til og med inneværende måned, og sammenligner mot det.
const PlummeAiAdvisorPlan = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const yearOf = (value) => /^\d{4}/.test(String(value || '')) ? Number(String(value).slice(0, 4)) : null;
  const keyOf = (line) => `${line.kind}:${line.accountCode}`;

  // Hvor mange måneder av året som er bokført. Et tidligere år er ferdig (12), et
  // fremtidig år har ingenting (0), og det pågående året teller til og med denne måneden.
  const monthsElapsedIn = (year, now = new Date()) => {
    if (year < now.getFullYear()) return 12;
    if (year > now.getFullYear()) return 0;
    return now.getMonth() + 1;
  };

  const budgetToDateOf = (line, months) => {
    const monthly = safeArray(line.monthlyAmounts);
    if (!monthly.length) {
      // Uten månedsfordeling er jevn fordeling det eneste forsvarlige anslaget.
      return months >= 12 ? num(line.annualAmount) : num(line.annualAmount) * (months / 12);
    }
    return monthly.slice(0, months).reduce((sum, value) => sum + num(value), 0);
  };

  // Et avvik er verdt å nevne når det både er stort nok i kroner og i andel. Uten
  // begge deler drukner de reelle avvikene i småbeløp fra små budsjettposter.
  const MATERIAL_MIN_AMOUNT = 1000;
  const MATERIAL_MIN_SHARE = 0.1;
  const isMaterial = (variance, budgetToDate) => {
    const abs = Math.abs(variance);
    if (abs < MATERIAL_MIN_AMOUNT) return false;
    if (budgetToDate <= 0) return true;
    return abs / budgetToDate >= MATERIAL_MIN_SHARE;
  };

  // Målfremdrift. `now` kommer fra bokførte tall via kalleren — brukeren eier målet,
  // Plumme eier målingen. `onTrack` sammenligner fremdrift mot hvor langt inn i
  // perioden vi er, ikke bare mot 100 %: 40 % av målet er bra i februar og dårlig i november.
  const buildGoals = ({ goals = [], metricNow = {}, year, now = new Date() }) => safeArray(goals)
    .filter((goal) => {
      if (!Number.isInteger(Number(year))) return true;
      const startYear = Number(goal.startYear) || yearOf(goal.createdAt);
      const completedYear = yearOf(goal.completedAt);
      return (!Number.isInteger(startYear) || startYear <= Number(year))
        && (!Number.isInteger(completedYear) || completedYear >= Number(year));
    }).map((goal) => {
    const current = num(metricNow[goal.metric]);
    const target = num(goal.target);
    const deadline = goal.deadline ? new Date(`${goal.deadline}T00:00:00`) : null;
    const created = goal.createdAt ? new Date(goal.createdAt) : null;
    const daysLeft = deadline && !Number.isNaN(deadline.getTime())
      ? Math.ceil((deadline.setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / 86400000)
      : null;
    const progress = target > 0 ? Math.max(0, Math.min(1, current / target)) : null;
    let timeElapsed = null;
    if (created && deadline && !Number.isNaN(created.getTime())) {
      const span = new Date(`${goal.deadline}T00:00:00`) - created;
      timeElapsed = span > 0 ? Math.max(0, Math.min(1, (new Date(now) - created) / span)) : 1;
    }
    return {
      ...goal,
      current,
      progress,
      daysLeft,
      overdue: daysLeft != null && daysLeft < 0,
      reached: target > 0 && current >= target,
      completed: yearOf(goal.completedAt) != null && yearOf(goal.completedAt) <= Number(year),
      needsReview: yearOf(goal.deadline) != null && yearOf(goal.deadline) < Number(year)
        && !(yearOf(goal.completedAt) != null && yearOf(goal.completedAt) <= Number(year)),
      // Uten tidsreferanse kan vi ikke si om noe ligger foran eller bak — da sier vi
      // ingenting i stedet for å gjette.
      onTrack: progress == null || timeElapsed == null ? null : progress >= timeElapsed,
    };
  });

  // Planlagte hendelser lagt på kontantstrømkurven. Dette er det som gjør kurven til
  // en faktisk prognose: uten dem er den bare løpende saldo av bokførte tall, flat
  // etter dagens måned. Tidligere måneder hoppes over. Inneværende måned tas med:
  // brukeren registrerer her betalinger som ennå ikke er bokført.
  const applyEventsToForecast = ({ forecast = [], events = [], year, now = new Date() }) => {
    const firstPlannedMonth = year < now.getFullYear() ? 12 : year > now.getFullYear() ? 0 : now.getMonth();
    const relevant = safeArray(events).filter((event) => {
      const eventYear = Number(String(event.date).slice(0, 4));
      const eventMonth = Number(String(event.date).slice(5, 7)) - 1;
      return eventYear === year && eventMonth >= firstPlannedMonth && eventMonth <= 11;
    });
    if (!relevant.length) return { forecast: safeArray(forecast), applied: [] };
    let running = 0;
    const adjusted = safeArray(forecast).map((point, index) => {
      const delta = relevant
        .filter((event) => Number(String(event.date).slice(5, 7)) - 1 === index)
        .reduce((sum, event) => sum + num(event.amount), 0);
      running += delta;
      if (!running) return point;
      return { ...point, balance: Math.round(num(point.balance) + running), certainty: 'planned' };
    });
    return { forecast: adjusted, applied: relevant };
  };

  const buildBudgetVsActual = ({ year, budgetLines = [], actualLines = [], goals = [], events = [], metricNow = {}, now = new Date(), periodMonths = null }) => {
    const months = periodMonths == null ? monthsElapsedIn(year, now) : Math.max(0, Math.min(12, Number(periodMonths) || 0));
    const rows = new Map();
    safeArray(budgetLines).forEach((line) => {
      rows.set(keyOf(line), {
        key: keyOf(line),
        accountCode: line.accountCode,
        name: line.accountName || line.accountCode,
        kind: line.kind,
        budgetYear: Math.round(num(line.annualAmount)),
        budgetToDate: Math.round(budgetToDateOf(line, months)),
        actual: 0,
        budgeted: true,
      });
    });
    safeArray(actualLines).forEach((line) => {
      const key = keyOf(line);
      const existing = rows.get(key);
      if (existing) {
        existing.actual = Math.round(num(line.annualAmount));
        return;
      }
      // Bokført på en konto som ikke er budsjettert. Designet viser bare
      // budsjetterte linjer, men da forsvinner nettopp de utgiftene ingen planla.
      rows.set(key, {
        key,
        accountCode: line.accountCode,
        name: line.accountName || line.accountCode,
        kind: line.kind,
        budgetYear: 0,
        budgetToDate: 0,
        actual: Math.round(num(line.annualAmount)),
        budgeted: false,
      });
    });
    const list = [...rows.values()].map((row) => {
      const variance = row.actual - row.budgetToDate;
      // For inntekt er mindre enn budsjettert dårlig; for kostnad er mer dårlig.
      const unfavourable = row.kind === 'income' ? variance < 0 : variance > 0;
      return {
        ...row,
        variance,
        unfavourable,
        material: isMaterial(variance, row.budgetToDate),
      };
    }).sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));

    const sum = (kind, field) => list.filter((row) => row.kind === kind).reduce((total, row) => total + row[field], 0);
    return {
      year,
      monthsElapsed: months,
      // Ingen budsjettlinjer i det hele tatt er en egen tilstand: da er det ingenting
      // å sammenligne mot, og UI skal be om et budsjett i stedet for å vise 0-rader.
      hasBudget: safeArray(budgetLines).length > 0,
      rows: list,
      totals: {
        incomeBudgetToDate: sum('income', 'budgetToDate'),
        incomeActual: sum('income', 'actual'),
        costBudgetToDate: sum('cost', 'budgetToDate'),
        costActual: sum('cost', 'actual'),
      },
      // De største avvikene som faktisk er verdt å forklare.
      notable: list.filter((row) => row.material).slice(0, 3),
      unbudgeted: list.filter((row) => !row.budgeted && row.actual > 0),
      goals: buildGoals({ goals, metricNow, year,
        now: year < now.getFullYear() ? new Date(year, 11, 31, 12)
          : year > now.getFullYear() ? new Date(year, 0, 1, 12) : now }),
      events: safeArray(events).filter((event) => String(event.date || '').startsWith(`${year}-`))
        .slice().sort((a, b) => String(a.date).localeCompare(String(b.date))),
    };
  };

  return { buildBudgetVsActual, buildGoals, applyEventsToForecast, monthsElapsedIn, budgetToDateOf };
})();

if (typeof globalThis !== 'undefined') globalThis.PlummeAiAdvisorPlan = PlummeAiAdvisorPlan;
if (typeof module !== 'undefined' && module.exports) module.exports = PlummeAiAdvisorPlan;
